import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CopyPlus,
    EditIcon,
    Focus,
    Info,
    MoreVertical,
    Replace,
    Trash2,
} from 'lucide-react';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import CollapsibleBottomPanel from '~/components/CollapsibleBottomPanel';
import InlineEdit from '~/components/InlineEdit';
import NestedMenuItem from '~/components/Menu/NestedMenuItem';
import StructureColorPicker from '../ColorPicker';
import {
    DICOMApprovalStatus,
    isApprovalStatusEditable,
} from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useRoi from '~/hooks/structure-set/useRoi';
import useStructureNameValidation from '~/hooks/structure-set/useStructureNameValidation';
import useViewportFocus from '~/hooks/useViewportFocus';
import { openedStructureDeletionConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import StructureTooltipProperties from '../../StructureTooltipProperties';
import StructureInformation from './StructureInformation';

interface StructureDetailsProps {
    roi: RoiReadModel;
}

const structureDetailsTooltipSlotProps = {
    popper: {
        modifiers: [
            {
                name: 'offset' as const,
                options: { offset: [0, 20] },
            },
        ],
        sx: { zIndex: 1500 },
    },
    tooltip: {
        sx: (theme: Theme) => ({
            py: 2,
            px: 2,
            borderRadius: 0,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
        }),
    },
};

export default function StructureDetails({ roi }: StructureDetailsProps) {
    const intl = useIntl();
    const {
        setInterpretedType,
        setPointCoordinates,
        getRoiVolumeCm3,
        getRoiEquivalentSphereDiameterCm,
        copyRoi,
        copySdfOver,
        setName,
        setColor,
        setVisible,
    } = useRoi(roi);
    const { focusOnStructure, focusOnPoint } = useViewportFocus();
    const setOpenedStructureDeletionConfirmationDialog = useSetRecoilState(
        openedStructureDeletionConfirmationDialogState
    );
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const [isRenaming, setIsRenaming] = useState(false);
    const colorPickerAnchorRef = useRef<HTMLDivElement>(null);
    const validateName = useStructureNameValidation(roi);

    const structureCanBeEdited = isApprovalStatusEditable(roi.approvalStatus);
    const isTarget = useDoseTargetLookup().has(roi.name);
    const structureCanBeDeleted = isApprovalStatusEditable(roi.approvalStatus);
    const hasContourData = roi.contourCount > 0;
    const structureSetUid = selectedStructureSet?.dicomUid ?? '';

    const handleDelete = () => {
        setOpenedStructureDeletionConfirmationDialog(roi.roiNumber);
    };

    const handleFocus = () => {
        // Focusing on a hidden ROI shows nothing useful — flip visibility on
        // first, mirroring the inline focus icon behaviour in StructureField.
        if (!roi.visible) {
            setVisible(true);
        }
        if (roi.isPointRoi && roi.pointCoordinatesMm) {
            focusOnPoint(roi.pointCoordinatesMm);
        } else {
            focusOnStructure(roi.roiNumber);
        }
    };

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleRename = () => {
        handleMenuClose();
        setIsRenaming(true);
    };

    const handleDuplicate = () => {
        if (!structureSetUid) {
            return;
        }
        handleMenuClose();
        copyRoi(structureSetUid);
    };

    const handleCopyOver = (targetRoiNumber: number) => {
        if (!structureSetUid) {
            return;
        }
        handleMenuClose();
        copySdfOver(structureSetUid, targetRoiNumber);
    };

    const otherRois = selectedStructureSet?.roiArray.filter(
        (r) => r.roiNumber !== roi.roiNumber
    );

    const content = roi ? (
        <CollapsibleBottomPanel
            header={
                <Box
                    ref={colorPickerAnchorRef}
                    display="grid"
                    gridTemplateColumns="auto 1fr"
                    alignItems="center"
                    gap={1}
                >
                    <StructureColorPicker
                        roi={roi}
                        onChange={setColor}
                        fieldAnchorRef={colorPickerAnchorRef}
                        disabled={!structureCanBeEdited}
                        popperPlacement="top-start"
                        isTarget={isTarget}
                    />
                    {isRenaming ? (
                        <InlineEdit
                            value={roi.name}
                            onSubmit={setName}
                            onCancel={() => setIsRenaming(false)}
                            validate={validateName}
                            fontSize={13}
                            fontWeight={500}
                        />
                    ) : (
                        <Typography
                            variant="body1"
                            fontSize={13}
                            fontWeight={500}
                            title={roi.name}
                            data-testid={`structure-details-name-${roi.name}`}
                            onDoubleClick={
                                structureCanBeEdited
                                    ? () => setIsRenaming(true)
                                    : undefined
                            }
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                cursor: structureCanBeEdited
                                    ? 'pointer'
                                    : undefined,
                            }}
                        >
                            {roi.name}
                        </Typography>
                    )}
                </Box>
            }
            headerActions={
                <>
                    <Tooltip title={<FormattedMessage id="structure.focus" />}>
                        <IconButton
                            size="small"
                            onClick={handleFocus}
                            sx={{ p: 0.25 }}
                            data-testid={`structure-details-focus-button-${roi.name}`}
                            aria-label={intl.formatMessage({
                                id: 'structure.focus',
                            })}
                        >
                            <Focus size={12} />
                        </IconButton>
                    </Tooltip>
                    {!roi.isPointRoi ? (
                        <Tooltip
                            title={
                                <StructureTooltipProperties
                                    roi={roi}
                                    volumeCm3={getRoiVolumeCm3()}
                                    equivalentSphereDiameterCm={getRoiEquivalentSphereDiameterCm()}
                                />
                            }
                            placement="right"
                            enterDelay={300}
                            leaveDelay={200}
                            slotProps={structureDetailsTooltipSlotProps}
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{ p: 0.25 }}
                                    data-testid={`structure-info-button-${roi.name}`}
                                    aria-label={intl.formatMessage({
                                        id: 'structureDetails',
                                    })}
                                >
                                    <Info size={12} />
                                </IconButton>
                                <Tooltip
                                    title={
                                        structureCanBeEdited ? (
                                            <FormattedMessage id="structure.rename" />
                                        ) : (
                                            <FormattedMessage id="structure.approved" />
                                        )
                                    }
                                >
                                    <Typography
                                        component="span"
                                        sx={{ display: 'inline-flex' }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={handleRename}
                                            disabled={!structureCanBeEdited}
                                            sx={{ p: 0.25 }}
                                            data-testid={`structure-edit-button-${roi.name}`}
                                            aria-label={intl.formatMessage({
                                                id: 'rename',
                                            })}
                                        >
                                            <EditIcon size={12} />
                                        </IconButton>
                                    </Typography>
                                </Tooltip>
                                <IconButton
                                    size="small"
                                    onClick={handleMenuOpen}
                                    sx={{ p: 0.25 }}
                                    data-testid="structure-more-menu-button"
                                    aria-label={intl.formatMessage({
                                        id: 'moreActions',
                                    })}
                                >
                                    <MoreVertical size={12} />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    ) : (
                        <>
                            <Tooltip
                                title={
                                    structureCanBeEdited ? (
                                        <FormattedMessage id="structure.rename" />
                                    ) : (
                                        <FormattedMessage id="structure.approved" />
                                    )
                                }
                            >
                                <Typography
                                    component="span"
                                    sx={{ display: 'inline-flex' }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handleRename}
                                        disabled={!structureCanBeEdited}
                                        sx={{ p: 0.25 }}
                                        data-testid={`structure-edit-button-${roi.name}`}
                                        aria-label={intl.formatMessage({
                                            id: 'rename',
                                        })}
                                    >
                                        <EditIcon size={12} />
                                    </IconButton>
                                </Typography>
                            </Tooltip>
                            <IconButton
                                size="small"
                                onClick={handleMenuOpen}
                                sx={{ p: 0.25 }}
                                data-testid="structure-more-menu-button"
                                aria-label={intl.formatMessage({
                                    id: 'moreActions',
                                })}
                            >
                                <MoreVertical size={12} />
                            </IconButton>
                        </>
                    )}
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={isMenuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        {!roi.isPointRoi && (
                            <MenuItem
                                onClick={handleDuplicate}
                                disabled={!hasContourData || !structureSetUid}
                            >
                                <ListItemIcon>
                                    <CopyPlus size={12} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="structure.duplicate" />
                                </ListItemText>
                            </MenuItem>
                        )}
                        {!roi.isPointRoi &&
                            otherRois &&
                            otherRois.length > 0 && (
                                <NestedMenuItem
                                    label={
                                        <FormattedMessage id="structure.replaceAnotherStructure" />
                                    }
                                    icon={<Replace size={12} />}
                                >
                                    {otherRois.map((target) => (
                                        <MenuItem
                                            key={target.roiNumber}
                                            onClick={() =>
                                                handleCopyOver(target.roiNumber)
                                            }
                                            disabled={
                                                !hasContourData ||
                                                !structureSetUid ||
                                                target.approvalStatus ===
                                                    DICOMApprovalStatus.APPROVED
                                            }
                                        >
                                            <ListItemText>
                                                {target.name}
                                            </ListItemText>
                                        </MenuItem>
                                    ))}
                                </NestedMenuItem>
                            )}
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                handleDelete();
                            }}
                            disabled={!structureCanBeDeleted}
                            data-testid="delete-structure-button"
                        >
                            <ListItemIcon>
                                <Trash2 size={12} />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="structure.delete" />
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </>
            }
        >
            <StructureInformation
                roi={roi}
                isEditable={structureCanBeEdited}
                onInterpretedTypeChange={setInterpretedType}
                getRoiVolumeCm3={getRoiVolumeCm3()}
                onPointCoordinatesChange={setPointCoordinates}
            />
        </CollapsibleBottomPanel>
    ) : (
        <Typography variant="body2">
            No structure selected.
            <br />
            In order to start reviewing select one structure from the list.
        </Typography>
    );

    return <div>{content}</div>;
}
